<template>
  <div class="plan-toolbar">
    <button class="plan-tb-arr" @click="$emit('navigate', -1)">
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        fill="none"
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
    <button class="plan-tb-arr" @click="$emit('navigate', 1)">
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        fill="none"
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>

    <div class="plan-tb-date" @click="togglePicker" ref="datePill">
      <span>{{ dateRangeLabel }}</span>
      <span class="plan-tb-caret">▾</span>

      <div v-if="pickerOpen" class="plan-dp-popup" @click.stop>
        <div class="plan-dp-hdr">
          <div class="plan-dp-nav">
            <button class="plan-dp-nbtn" @click="picPrevMonth">&#8249;</button>
            <span class="plan-dp-mon-lbl">{{ monthNames[pickerMonth] }}</span>
            <button class="plan-dp-nbtn" @click="picNextMonth">&#8250;</button>
          </div>
          <div class="plan-dp-nav">
            <button class="plan-dp-nbtn" @click="picPrevYear">&#8249;</button>
            <span class="plan-dp-yr-lbl">{{ pickerYear }}</span>
            <button class="plan-dp-nbtn" @click="picNextYear">&#8250;</button>
          </div>
        </div>
        <div class="plan-dp-grid">
          <div class="plan-dp-dow-row">
            <div
              v-for="d in ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']"
              :key="d"
              class="plan-dp-dow"
            >
              {{ d }}
            </div>
          </div>
          <div class="plan-dp-day-grid">
            <div
              v-for="cell in calendarCells"
              :key="cell.key"
              class="plan-dp-day"
              :class="cell.classes"
              @click="pickDate(cell.date)"
            >
              {{ cell.date.getDate() }}
            </div>
          </div>
        </div>
        <div class="plan-dp-today-row">
          <span class="plan-dp-today-lnk" @click="selectToday">TODAY</span>
        </div>
      </div>
    </div>

    <button class="plan-tb-today" @click="selectToday">TODAY</button>

    <div class="plan-tb-spacer"></div>

    <button class="plan-tb-print" @click="$emit('print')">
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2"
        fill="none"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="6 9 6 2 18 2 18 9" />
        <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
        <rect x="6" y="14" width="12" height="8" />
      </svg>
      Print
    </button>

    <div class="plan-tb-views">
      <button
        class="plan-tb-view"
        :class="{ active: viewMode === 'day' }"
        @click="$emit('set-view', 'day')"
      >
        DAY
      </button>
      <button
        class="plan-tb-view"
        :class="{ active: viewMode === 'workweek' }"
        @click="$emit('set-view', 'workweek')"
      >
        WORK WEEK
      </button>
      <button
        class="plan-tb-view"
        :class="{ active: viewMode === 'week' }"
        @click="$emit('set-view', 'week')"
      >
        WEEK
      </button>
    </div>

    <div>
      <DxButton
        type="normal"
        styling-mode="contained"
        class="button-full-width override-styles"
        icon="filter"
        @click="openFilterPanel()"
        width="30"
        v-if="!isFilterOpen"
      />
    </div>
  </div>
</template>

<script>
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function mondayOf(dt) {
  const r = new Date(dt);
  const day = r.getDay();
  r.setDate(r.getDate() - (day === 0 ? 6 : day - 1));
  return r;
}

export default {
  name: "PlanToolbar",

  props: {
    currentDate: { type: Date, required: true },
    viewMode: { type: String, required: true },
    showRightContainer: { type: Boolean, required: true },
  },

  emits: [
    "navigate",
    "set-view",
    "date-selected",
    "go-today",
    "set-show-right-container",
    "print",
  ],

  data() {
    return {
      pickerOpen: false,
      pickerMonth: this.currentDate.getMonth(),
      pickerYear: this.currentDate.getFullYear(),
      monthNames: MONTH_NAMES,
    };
  },

  computed: {
    isFilterOpen() {
      return this.showRightContainer; // Placeholder for actual filter state
    },
    periodStart() {
      if (this.viewMode === "day") {
        const d = this.currentDate;
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
      }
      return mondayOf(this.currentDate);
    },

    periodEnd() {
      const span =
        this.viewMode === "day" ? 1 : this.viewMode === "workweek" ? 5 : 6;
      const d = new Date(this.periodStart);
      d.setDate(d.getDate() + span - 1);
      return d;
    },

    dateRangeLabel() {
      const fmt = (dt) =>
        `${String(dt.getDate()).padStart(2, "0")}-${
          MONTH_NAMES[dt.getMonth()]
        }-${dt.getFullYear()}`;

      if (this.viewMode === "day") return fmt(this.periodStart);
      return `${fmt(this.periodStart)} - ${fmt(this.periodEnd)}`;
    },

    calendarCells() {
      const year = this.pickerYear;
      const month = this.pickerMonth;
      const firstDay = new Date(year, month, 1).getDay();
      const daysInMon = new Date(year, month + 1, 0).getDate();
      const prevDays = new Date(year, month, 0).getDate();
      const today = new Date();
      const rangeStart = this.periodStart;
      const rangeEnd = this.periodEnd;
      const singleDay = this.viewMode === "day";
      const cells = [];

      for (let i = firstDay - 1; i >= 0; i--) {
        cells.push({
          key: `p${i}`,
          date: new Date(year, month - 1, prevDays - i),
          classes: ["other"],
        });
      }

      for (let d = 1; d <= daysInMon; d++) {
        const date = new Date(year, month, d);
        const classes = [];
        if (isSameDay(date, today)) classes.push("today");

        if (singleDay) {
          if (isSameDay(date, rangeStart)) classes.push("sel");
        } else if (isSameDay(date, rangeStart)) {
          classes.push("rng-s");
        } else if (isSameDay(date, rangeEnd)) {
          classes.push("rng-e");
        } else if (date > rangeStart && date < rangeEnd) {
          classes.push("in-rng");
        }

        cells.push({ key: `c${d}`, date, classes });
      }

      const remainder = 42 - cells.length;
      for (let n = 1; n <= remainder; n++) {
        cells.push({
          key: `n${n}`,
          date: new Date(year, month + 1, n),
          classes: ["other"],
        });
      }

      return cells;
    },
  },

  mounted() {
    document.addEventListener("click", this.closePicker);
  },

  beforeDestroy() {
    document.removeEventListener("click", this.closePicker);
  },

  methods: {
    openFilterPanel() {
      this.$emit("set-show-right-container", true);
    },
    togglePicker() {
      this.pickerOpen = !this.pickerOpen;
      if (this.pickerOpen) {
        this.pickerMonth = this.currentDate.getMonth();
        this.pickerYear = this.currentDate.getFullYear();
      }
    },

    closePicker(e) {
      if (this.$refs.datePill && !this.$refs.datePill.contains(e.target)) {
        this.pickerOpen = false;
      }
    },

    picPrevMonth() {
      if (this.pickerMonth === 0) {
        this.pickerMonth = 11;
        this.pickerYear--;
      } else {
        this.pickerMonth--;
      }
    },

    picNextMonth() {
      if (this.pickerMonth === 11) {
        this.pickerMonth = 0;
        this.pickerYear++;
      } else {
        this.pickerMonth++;
      }
    },

    picPrevYear() {
      this.pickerYear--;
    },

    picNextYear() {
      this.pickerYear++;
    },

    pickDate(date) {
      this.pickerOpen = false;
      this.$emit("date-selected", new Date(date));
    },

    selectToday() {
      this.pickerOpen = false;
      this.$emit("go-today");
    },
  },
};
</script>

<style scoped lang="scss">
.plan-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  height: 46px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.plan-tb-arr {
  width: 26px;
  height: 26px;
  border: 1px solid #d1d5db;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  &:hover {
    background: #f9fafb;
  }
}

.plan-tb-date {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 1px solid #d1d5db;
  background: #fff;
  font-size: 13px;
  color: #111827;
  cursor: pointer;
  border-radius: 3px;
  position: relative;
  white-space: nowrap;
  &:hover {
    background: #f9fafb;
  }
}
.plan-tb-caret {
  color: #9ca3af;
  font-size: 10px;
  margin-left: 3px;
}

.plan-tb-today {
  padding: 4px 9px;
  border: 1px solid #d1d5db;
  background: #fff;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;
  border-radius: 2px;
  letter-spacing: 0.05em;
  &:hover {
    background: #f9fafb;
    color: #374151;
  }
}

.plan-tb-spacer {
  flex: 1;
}

.plan-tb-print {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border: 1px solid #d1d5db;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  border-radius: 3px;
  font-family: "Roboto", sans-serif;
  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
}

.plan-dp-popup {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: #fff;
  border: 1px solid #d1d5db;
  z-index: 300;
  min-width: 270px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}
.plan-dp-hdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid #f3f4f6;
}
.plan-dp-nav {
  display: flex;
  align-items: center;
  gap: 6px;
}
.plan-dp-mon-lbl {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  min-width: 90px;
  display: inline-block;
}
.plan-dp-yr-lbl {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  min-width: 36px;
  text-align: center;
}
.plan-dp-nbtn {
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 14px;
  border-radius: 2px;
  &:hover {
    background: #f3f4f6;
  }
}
.plan-dp-grid {
  padding: 6px 10px 8px;
}
.plan-dp-dow-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 2px;
}
.plan-dp-dow {
  text-align: center;
  font-size: 11px;
  font-weight: 500;
  color: #9ca3af;
  padding: 2px 0;
}
.plan-dp-day-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}
.plan-dp-day {
  height: 30px;
  width: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #111827;
  cursor: pointer;
  border-radius: 50%;
  margin: 1px auto;
  &:hover {
    background: #e0f2fe;
  }
  &.other {
    color: #d1d5db;
  }
  &.today {
    background: #dbeafe;
    color: #2563eb;
    font-weight: 700;
  }
  &.sel {
    background: #2a95c5;
    color: #fff;
  }
  &.in-rng {
    background: #e0f2fe;
    border-radius: 0;
    width: 100%;
  }
  &.rng-s {
    background: #2a95c5;
    color: #fff;
    border-radius: 50% 0 0 50%;
    width: 100%;
  }
  &.rng-e {
    background: #2a95c5;
    color: #fff;
    border-radius: 0 50% 50% 0;
    width: 100%;
  }
}
.plan-dp-today-row {
  text-align: center;
  padding: 5px 12px 7px;
  border-top: 1px solid #f3f4f6;
}
.plan-dp-today-lnk {
  font-size: 12px;
  color: #d92d20;
  font-weight: 700;
  cursor: pointer;
}

.plan-tb-views {
  display: flex;
  gap: 2px;
}
.plan-tb-view {
  padding: 5px 8px;
  border: none;
  background: transparent;
  font-size: 13px;
  color: #6b7280;
  cursor: pointer;
  white-space: nowrap;
  font-family: "Roboto", sans-serif;
  &:hover {
    color: #111827;
  }
  &.active {
    color: #2a95c5;
    font-weight: 500;
    background: transparent;
  }
}
</style>
